import React, { useEffect, useState } from 'react';
import { X, ArrowUpRight } from 'lucide-react';
import { SHOWCASE } from '../mock';
import { listProjects } from '../api';

export default function Showcase() {
  const [active, setActive] = useState(null);
  const [projects, setProjects] = useState(null); // null = still loading

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listProjects();
        if (!cancelled) setProjects(list);
      } catch (e) {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build the tiles: prefer uploaded projects, fall back to stock cinematic images so the page never looks empty.
  const useUploaded = Array.isArray(projects) && projects.length > 0;
  const tiles = useUploaded
    ? projects.map((p, i) => ({ id: p.id, src: p.image_url, title: p.title, subtitle: p.subtitle, index: i }))
    : SHOWCASE.map((src, i) => ({ id: `stock-${i}`, src, title: `Project ${String(i + 1).padStart(2, '0')}`, subtitle: '', index: i }));

  return (
    <section id="gallery" className="relative py-20 md:py-28">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#162028]/60 mb-4 flex items-center gap-2">
              <span className="h-px w-8 bg-[#49001B]" /> Portfolio
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight text-[#162028] max-w-3xl">
              Showcase of <span className="italic text-[#49001B]">success.</span>
            </h2>
          </div>
          <p className="text-[15px] leading-relaxed text-[#162028]/70 max-w-md">
            Explore our portfolio — from groundbreaking marketing campaigns to transformative branding initiatives. Each project reflects our commitment to excellence and creativity.
          </p>
        </div>

        {/* Masonry-like grid */}
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 md:gap-5 [column-fill:_balance]">
          {tiles.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t)}
              className="tile-hover mb-4 md:mb-5 relative block w-full overflow-hidden rounded-2xl bg-[#162028]/5 group text-left"
            >
              <img src={t.src} alt={t.title} className="w-full h-auto block" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#162028]/85 via-[#162028]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                <div className="min-w-0 pr-2">
                  <div className="font-serif-display text-[15px] leading-tight text-[#ede2c4] truncate">{t.title}</div>
                  {t.subtitle ? <div className="text-[10px] uppercase tracking-[0.22em] text-[#ddc390] truncate mt-0.5">{t.subtitle}</div> : <div className="text-[10px] uppercase tracking-[0.22em] text-[#ddc390]">View</div>}
                </div>
                <span className="shrink-0 h-8 w-8 rounded-full bg-[#ddc390] flex items-center justify-center text-[#162028]">
                  <ArrowUpRight size={15} />
                </span>
              </div>
            </button>
          ))}
        </div>

        {!useUploaded && projects && (
          <p className="mt-8 text-center text-[12px] uppercase tracking-[0.22em] text-[#162028]/50">
            Sample layout — upload real projects from <a href="/admin" className="underline decoration-[#49001B] underline-offset-4 hover:text-[#49001B]">/admin</a>
          </p>
        )}
      </div>

      {/* Lightbox */}
      {active && (
        <div className="fixed inset-0 z-[80] bg-[#162028]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10" onClick={() => setActive(null)}>
          <button onClick={() => setActive(null)} className="absolute top-5 right-5 h-11 w-11 rounded-full bg-[#ede2c4] text-[#162028] flex items-center justify-center hover:bg-[#49001B] hover:text-[#ede2c4] transition-colors" aria-label="Close">
            <X size={18} />
          </button>
          <div className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img src={active.src} alt={active.title} className="max-h-[80vh] max-w-full rounded-2xl shadow-2xl object-contain" />
            <div className="text-center">
              <div className="font-serif-display text-2xl text-[#ede2c4]">{active.title}</div>
              {active.subtitle ? <div className="text-[11px] uppercase tracking-[0.24em] text-[#ddc390] mt-1">{active.subtitle}</div> : null}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
